import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import axios from "axios";

import { api, setAccessToken, setSessionLostHandler } from "@/lib/api";
import {
  obrisiIdentitet,
  procitajIdentitet,
  spremiIdentitet,
} from "@/lib/identitet";

export interface Role {
  code: string;
  name_hr: string;
}

export interface Permission {
  module: string;
  action: string;
  scope: "all" | "team" | "own";
}

export interface CurrentUser {
  id: string;
  email: string;
  full_name: string;
  is_active: boolean;
  auth_provider: string;
  mfa_confirmed: boolean;
  mfa_setup_required: boolean;
  must_change_password: boolean;
  roles: Role[];
  permissions: Permission[];
}

/** What `POST /auth/login` answers with when a second factor is required. */
export interface MfaChallenge {
  mfa_required: true;
  mfa_token: string;
  mfa_setup_required: boolean;
}

interface TokenResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
}

type LoginResult =
  | { status: "signed-in" }
  | { status: "mfa"; mfaToken: string }
  | { status: "mfa-setup"; mfaToken: string };

interface AuthState {
  user: CurrentUser | null;
  /** True until the initial session check finishes, so nothing flashes. */
  loading: boolean;
  /**
   * The session was restored from the device because the server could not be
   * reached (ADR-0004 §2.7). Everything on screen is a local copy, and the
   * only thing that can be done with it is add to the queue.
   */
  izvanMreze: boolean;
  login: (email: string, password: string) => Promise<LoginResult>;
  verifyTotp: (mfaToken: string, code: string) => Promise<void>;
  verifyBackupCode: (mfaToken: string, backupCode: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  can: (module: string, action: string) => boolean;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [izvanMreze, setIzvanMreze] = useState(false);

  const loadUser = useCallback(async () => {
    const { data } = await api.get<CurrentUser>("/auth/me");
    setUser(data);
    setIzvanMreze(false);
    // Kept so the next start can show this person their site without a
    // server (§2.7). Their own account, on their own phone.
    await spremiIdentitet(data).catch(() => undefined);
  }, []);

  const finishSignIn = useCallback(
    async (token: string) => {
      setAccessToken(token);
      await loadUser();
    },
    [loadUser],
  );

  // On first load the access token is gone - it only ever lived in memory -
  // but the refresh cookie may still be valid, so a reload should not force a
  // fresh login.
  useEffect(() => {
    let cancelled = false;

    const restore = async () => {
      try {
        const { data } = await api.post<TokenResponse>("/auth/refresh");
        if (!cancelled) {
          await finishSignIn(data.access_token);
        }
      } catch (error: unknown) {
        if (cancelled) {
          return;
        }
        setAccessToken(null);

        // The same failure means two opposite things, and which one decides
        // whether the site manager keeps his screen or is sent to sign in.
        //
        // The distinction is not `navigator.onLine`. That flag reports
        // whether the device has *a* network, not whether this server can be
        // reached, and it is wrong often enough - captive portals, emulated
        // conditions, a VPN coming up - that a feature resting on it fails in
        // exactly the field conditions it exists for. What is reliable is
        // whether an answer arrived: a refusal has a response, an unreachable
        // server does not.
        const bezOdgovora = axios.isAxiosError(error) && !error.response;
        const spremljen = bezOdgovora
          ? await procitajIdentitet().catch(() => null)
          : null;
        if (cancelled) {
          return;
        }
        setUser(spremljen);
        setIzvanMreze(spremljen !== null);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void restore();
    return () => {
      cancelled = true;
    };
  }, [finishSignIn]);

  useEffect(() => {
    setSessionLostHandler(() => {
      setUser(null);
      setIzvanMreze(false);
    });
    return () => setSessionLostHandler(null);
  }, []);

  // A session restored from the device is provisional. The moment there is a
  // connection it is checked against the server, which either confirms it or
  // sends the manager to the login screen - and only then can the queue go
  // (§2.7). Nothing here touches the queue either way.
  useEffect(() => {
    if (!izvanMreze) {
      return;
    }

    const provjeri = () => {
      void api
        .post<TokenResponse>("/auth/refresh")
        .then((odgovor) => finishSignIn(odgovor.data.access_token))
        .catch(() => {
          setAccessToken(null);
          setUser(null);
          setIzvanMreze(false);
        });
    };

    window.addEventListener("online", provjeri);
    return () => window.removeEventListener("online", provjeri);
  }, [izvanMreze, finishSignIn]);

  const login = useCallback(
    async (email: string, password: string): Promise<LoginResult> => {
      const { data } = await api.post<TokenResponse | MfaChallenge>(
        "/auth/login",
        { email, password },
      );

      if ("mfa_required" in data) {
        return data.mfa_setup_required
          ? { status: "mfa-setup", mfaToken: data.mfa_token }
          : { status: "mfa", mfaToken: data.mfa_token };
      }

      await finishSignIn(data.access_token);
      return { status: "signed-in" };
    },
    [finishSignIn],
  );

  const verifyTotp = useCallback(
    async (mfaToken: string, code: string) => {
      const { data } = await api.post<TokenResponse>("/auth/mfa/verify", {
        mfa_token: mfaToken,
        code,
      });
      await finishSignIn(data.access_token);
    },
    [finishSignIn],
  );

  const verifyBackupCode = useCallback(
    async (mfaToken: string, backupCode: string) => {
      const { data } = await api.post<TokenResponse>(
        "/auth/mfa/verify-recovery",
        { mfa_token: mfaToken, recovery_code: backupCode },
      );
      await finishSignIn(data.access_token);
    },
    [finishSignIn],
  );

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      // Whatever the server says, this browser is signing out. A failed
      // logout that leaves the user apparently signed in is worse than a
      // stale token on the server, which expires on its own.
    } finally {
      setAccessToken(null);
      setUser(null);
      setIzvanMreze(false);
      // Signing out is a person's decision, unlike a token running out, so it
      // leaves no session for the next start to resume. The queue is
      // untouched: unsent work is not this account's to discard (§2.7).
      await obrisiIdentitet().catch(() => undefined);
    }
  }, []);

  const can = useCallback(
    (module: string, action: string) =>
      user?.permissions.some(
        (permission) =>
          permission.module === module && permission.action === action,
      ) ?? false,
    [user],
  );

  const value = useMemo(
    () => ({
      user,
      loading,
      izvanMreze,
      login,
      verifyTotp,
      verifyBackupCode,
      logout,
      refreshUser: loadUser,
      can,
    }),
    [
      user,
      loading,
      izvanMreze,
      login,
      verifyTotp,
      verifyBackupCode,
      logout,
      loadUser,
      can,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used inside AuthProvider");
  }
  return context;
}
